import { Box, Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { useDPFormSuggestionsContext } from '@/modules/aiDP/context/dpFormSuggestions.context';
import { DPFormRecipientSuggestionsMatcher } from '@/modules/aiDP/DPFormRecipientSuggestionsMatcher';
import { AISuggestionsMatcherProvider } from '@/modules/aiSuggestions/AISuggestionsMatcher/context/AISuggestionsMatcher.provider';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { HypotheticalWaterfallScenarioCallout } from '@/modules/dispositiveProvisions/components/HypotheticalWaterfallScenarioCallout';
import { useDispositiveDiagramSyncContext } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { useActiveClientProfile } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/utilityHooks';
import { getTileIconTypenameFromPulid } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/DispositiveProvisionTileIcon';
import { DisposingObjectHeader } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/DisposingObjectHeader';
import { PrimaryClientDeathSection } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/PrimaryClientDeathSection';
import {
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import {
  getDeathEventBasePath,
  shouldShowFirstDeathSection,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { getEntityKindFromEntityType } from '@/modules/entities/utils/getEntityKindFromEntityType';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { getPulidKind } from '@/utils/pulid';

interface DispositiveProvisionsFormV2FormBodyProps {
  /** Callback when user clicks "Switch death order" in the opposite death order callout */
  onSwitchDeathOrder?: () => void;
}

export function DispositiveProvisionsFormV2FormBody({
  onSwitchDeathOrder,
}: DispositiveProvisionsFormV2FormBodyProps) {
  const {
    id,
    isTwoClientHousehold,
    entityType,
    entityDisplayName,
    isEditingRootTarget,
    isClientProfile,
    isOnHypotheticalWaterfall,
    waterfall,
  } = useDispositiveProvisionsContext();

  const { selectedNode } = useDispositiveDiagramSyncContext();
  const {
    loading: loadingDPSuggestions,
    hasUnacknowledgedKGV2RecipientSuggestions,
  } = useDPFormSuggestionsContext();

  // Determine which death event applies when editing a client profile
  const { isFirstToDie } = useActiveClientProfile();
  const canEditHouseholdData = useCanEditDispositiveProvisions();
  const useKGV2DPSuggestions = useFeatureFlag('aikg_v2_associations_poc');

  const { control } = useFormContext<DispositiveProvisionsFormV2Shape>();

  // Watch both death event recipients to determine if we need to show both
  // sections for client profiles. AI-extracted dispositions may be placed on
  // the "wrong" death event; without rendering that section the user cannot
  // view or delete them.
  const [firstDeathRecipients, secondDeathRecipients] = useWatch({
    control,
    name: [
      `${getDeathEventBasePath(PrimaryClientDeathEvent.FIRST_DEATH)}.recipients`,
      `${getDeathEventBasePath(PrimaryClientDeathEvent.SECOND_DEATH)}.recipients`,
    ],
  });

  const semanticTileKindNode = useMemo(() => {
    return {
      __typename: getTileIconTypenameFromPulid(getPulidKind(id)),
      entityKind: entityType
        ? getEntityKindFromEntityType(entityType)
        : undefined,
      isCharitableOrganization: false, // Organizations don't dispose of assets
    };
  }, [entityType, id]);

  const showFirstDeathSection = useMemo(
    () =>
      shouldShowFirstDeathSection({
        id,
        isEditingRootTarget,
        hasIncomingPouroverDisposition:
          !!selectedNode?.node?.data?.hasIncomingPouroverDisposition,
        isClientProfile,
        isTwoClientHousehold,
        isFirstToDie,
        firstDeathRecipientsCount: firstDeathRecipients?.length ?? 0,
        secondDeathRecipientsCount: secondDeathRecipients?.length ?? 0,
      }),
    [
      id,
      isEditingRootTarget,
      selectedNode?.node?.data?.hasIncomingPouroverDisposition,
      isClientProfile,
      isFirstToDie,
      isTwoClientHousehold,
      firstDeathRecipients?.length,
      secondDeathRecipients?.length,
    ]
  );

  const shouldRenderSectionScopedMatchers =
    canEditHouseholdData &&
    useKGV2DPSuggestions &&
    hasUnacknowledgedKGV2RecipientSuggestions;
  const shouldRenderLegacyMatcher =
    canEditHouseholdData &&
    (!useKGV2DPSuggestions ||
      (!loadingDPSuggestions && !hasUnacknowledgedKGV2RecipientSuggestions));

  const getSuggestionsMatcher = useCallback(
    (deathEvent: PrimaryClientDeathEvent) => {
      if (!shouldRenderSectionScopedMatchers) {
        return null;
      }

      return (
        <AISuggestionsMatcherProvider>
          <DPFormRecipientSuggestionsMatcher deathEvent={deathEvent} />
        </AISuggestionsMatcherProvider>
      );
    },
    [shouldRenderSectionScopedMatchers]
  );

  const renderPrimaryClientDeathSection = (
    deathEvent: PrimaryClientDeathEvent
  ) => {
    return (
      <PrimaryClientDeathSection
        deathEvent={deathEvent}
        node={semanticTileKindNode}
        suggestionsMatcher={getSuggestionsMatcher(deathEvent)}
      />
    );
  };

  return (
    <Stack direction="column" sx={{ pt: 3 }}>
      {isOnHypotheticalWaterfall && waterfall?.displayName && (
        <Box px={2}>
          <HypotheticalWaterfallScenarioCallout
            waterfallName={waterfall.displayName}
          />
        </Box>
      )}
      <DisposingObjectHeader
        displayName={entityDisplayName}
        node={semanticTileKindNode}
      />
      {shouldRenderLegacyMatcher && (
        <AISuggestionsMatcherProvider>
          <DPFormRecipientSuggestionsMatcher
            onSwitchDeathOrder={onSwitchDeathOrder}
          />
        </AISuggestionsMatcherProvider>
      )}
      {showFirstDeathSection ? (
        <>
          {renderPrimaryClientDeathSection(PrimaryClientDeathEvent.FIRST_DEATH)}
          {isTwoClientHousehold &&
            renderPrimaryClientDeathSection(
              PrimaryClientDeathEvent.SECOND_DEATH
            )}
        </>
      ) : (
        renderPrimaryClientDeathSection(
          (() => {
            if (isClientProfile) {
              return isFirstToDie
                ? PrimaryClientDeathEvent.FIRST_DEATH
                : PrimaryClientDeathEvent.SECOND_DEATH;
            }
            // For testamentary entities or other non-entity targets:
            // two-grantor households default to second death (TEs distribute after second death),
            // single-grantor households use first death (only one death event exists).
            return isTwoClientHousehold
              ? PrimaryClientDeathEvent.SECOND_DEATH
              : PrimaryClientDeathEvent.FIRST_DEATH;
          })()
        )
      )}
    </Stack>
  );
}
